/**
 * Synthesized sound effects (docs/11-ux-ui.md §5): WebAudio oscillators,
 * no audio files. Off until the player enables sound in Settings.
 */
export type Sfx = 'tap' | 'flip' | 'rare' | 'hit' | 'crit' | 'miss' | 'victory' | 'defeat' | 'coin' | 'levelup' | 'error';

let enabled = false;
let ctx: AudioContext | null = null;

export function setSoundEnabled(on: boolean): void {
  enabled = on;
}

function audio(): AudioContext | null {
  if (!enabled) return null;
  const Ctor = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
  if (!Ctor) return null;
  ctx ??= new Ctor();
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

function tone(ac: AudioContext, freq: number, start: number, dur: number, type: OscillatorType = 'sine', gain = 0.12, endFreq?: number): void {
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, ac.currentTime + start);
  if (endFreq) osc.frequency.exponentialRampToValueAtTime(endFreq, ac.currentTime + start + dur);
  g.gain.setValueAtTime(0.0001, ac.currentTime + start);
  g.gain.exponentialRampToValueAtTime(gain, ac.currentTime + start + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + start + dur);
  osc.connect(g).connect(ac.destination);
  osc.start(ac.currentTime + start);
  osc.stop(ac.currentTime + start + dur + 0.02);
}

export function play(name: Sfx): void {
  const ac = audio();
  if (!ac) return;
  switch (name) {
    case 'tap':
      tone(ac, 640, 0, 0.05, 'sine', 0.06);
      break;
    case 'flip':
      tone(ac, 700, 0, 0.08, 'triangle', 0.07, 1250);
      break;
    case 'rare':
      [523, 659, 784, 1046, 1318].forEach((f, i) => tone(ac, f, i * 0.07, 0.22, 'triangle', 0.09));
      break;
    case 'hit':
      tone(ac, 180, 0, 0.09, 'square', 0.05, 70);
      break;
    case 'crit':
      tone(ac, 260, 0, 0.14, 'sawtooth', 0.07, 60);
      tone(ac, 1400, 0, 0.05, 'square', 0.03);
      break;
    case 'miss':
      tone(ac, 900, 0, 0.12, 'sine', 0.03, 500);
      break;
    case 'victory':
      [523, 659, 784, 1046].forEach((f, i) => tone(ac, f, i * 0.11, 0.3, 'triangle', 0.1));
      break;
    case 'defeat':
      [392, 330, 262].forEach((f, i) => tone(ac, f, i * 0.16, 0.34, 'triangle', 0.08));
      break;
    case 'coin':
      tone(ac, 988, 0, 0.08, 'square', 0.04);
      tone(ac, 1319, 0.07, 0.16, 'square', 0.04);
      break;
    case 'levelup':
      [392, 523, 659, 784, 1046, 1318].forEach((f, i) => tone(ac, f, i * 0.06, 0.28, 'triangle', 0.08));
      break;
    case 'error':
      tone(ac, 220, 0, 0.12, 'square', 0.04, 160);
      break;
  }
}

export function vibrate(pattern: number | number[]): void {
  if (!enabled) return;
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* unsupported */
  }
}
